/**
 * The viewfinder.
 *
 * getUserMedia, not a file input. The pixels go straight from the camera stream
 * into this page, so they never touch the filesystem and cannot appear in the
 * user's camera roll — the guarantee holds by construction rather than by trusting
 * platform behaviour.
 *
 * The cost is the native camera's processing pipeline, so we ask for the largest
 * track the device will give us. On a current phone that is around eight
 * megapixels, which is ample for a receipt, a whiteboard or a stamped part number.
 */

const CONSTRAINTS: MediaStreamConstraints = {
  video: {
    facingMode: { ideal: 'environment' },
    width: { ideal: 3840 },
    height: { ideal: 2160 },
  },
  audio: false,
};

export interface Shot {
  bytes: Uint8Array;
  /** Small preview for the drawer. Sealed with the same content key, so shredding
   *  the key destroys the thumbnail along with the photo. */
  preview: Uint8Array;
  width: number;
  height: number;
}

export class Camera {
  #stream: MediaStream | null = null;
  #video: HTMLVideoElement | null = null;

  async start(video: HTMLVideoElement): Promise<void> {
    this.#stream = await navigator.mediaDevices.getUserMedia(CONSTRAINTS);
    this.#video = video;
    video.srcObject = this.#stream;
    video.muted = true;
    await video.play();
    // Safari reports zero dimensions until the first frame has been decoded.
    if (!video.videoWidth) {
      await new Promise<void>((r) => video.addEventListener('loadeddata', () => r(), { once: true }));
    }
  }

  /** Full-resolution frame grab. No decisions here: tagging happens afterwards. */
  async shoot(quality = 0.92): Promise<Shot> {
    const video = this.#video;
    if (!video) throw new Error('The camera is not running');
    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d')!.drawImage(video, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', quality));
    if (!blob) throw new Error('Could not read a frame from the camera');

    // A 160px preview, so the drawer can be scanned without decrypting full frames.
    const scale = 160 / Math.max(width, height);
    const thumb = document.createElement('canvas');
    thumb.width = Math.max(1, Math.round(width * scale));
    thumb.height = Math.max(1, Math.round(height * scale));
    thumb.getContext('2d')!.drawImage(video, 0, 0, thumb.width, thumb.height);
    const previewBlob = await new Promise<Blob | null>((r) => thumb.toBlob(r, 'image/jpeg', 0.6));

    return {
      bytes: new Uint8Array(await blob.arrayBuffer()),
      preview: new Uint8Array(previewBlob ? await previewBlob.arrayBuffer() : []),
      width, height,
    };
  }

  /** Torch, where the device exposes it. Lofts and meter cupboards are dark. */
  async torch(on: boolean): Promise<boolean> {
    const track = this.#stream?.getVideoTracks()[0];
    if (!track) return false;
    try {
      await track.applyConstraints({ advanced: [{ torch: on }] } as MediaTrackConstraints);
      return true;
    } catch {
      return false;
    }
  }

  stop(): void {
    this.#stream?.getTracks().forEach((t) => t.stop());
    this.#stream = null;
    this.#video = null;
  }
}
